import React, {
	useEffect,
	useRef,
	useCallback,
	useState,
	useMemo,
} from 'react';
import {useTranslation} from 'react-i18next';
import useMrcStore from 'common/stores/mrcStore';
import {getDateDefaultFormat} from 'utils/date';
import {getAvgTimeToSign, getSigningProgressPercent} from '../util';

import {
	MrcScrollArea,
	MrcTh,
	MrcTr,
	MrcTable,
	MrcTableWrapper,
	MrcTd,
	MrcTrBody,
	MrcExpandedRowTd,
	MrcExpandedRowContent,
	MrcHeader,
} from './styled';
import {MrcColumn} from 'containers/MRC/types';
import {Layout, Text} from '@phonectas/unifon-ui-kit';
import {MrcItem} from 'common/types/store/mrcTypes';
import RejectIcon from 'common/components/icons/RejectIcon';
import NewCheckIcon from 'common/components/icons/NewCheckIcon';
import NewSCheduleIcon from 'common/components/icons/NewSCheduleIcon';

interface MrcTableProps<T> {
    columns: MrcColumn<T>[];
    data: T[];
    onLoadMore: () => void;
    isLoading?: boolean;
	totalCount?: number;
	onRowClick?: (row: T) => void;
    onExpand?: boolean;
	renderExpandedRow?: (row: T) => React.ReactNode;
	shouldCloseExpandedRows?: boolean;
}

type SortState = {key: string; dir: 'asc' | 'desc'} | null;

function MrcCustomTable<T extends MrcItem>({
	columns,
	data,
	onLoadMore,
	isLoading,
	totalCount,
	onRowClick,
	onExpand = false,
	renderExpandedRow,
	shouldCloseExpandedRows,
}: MrcTableProps<T>) {
	const [t] = useTranslation();
	const {setActiveMrcRow, resetMrcActiveRow} = useMrcStore();
	const scrollRef = useRef<HTMLDivElement>(null);
	const [sort, setSort] = useState<SortState>(null);
	const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

	useEffect(() => {
		if (shouldCloseExpandedRows) {
			setExpandedRows(new Set());
		}
	}, [shouldCloseExpandedRows]);
	// Sort data locally in the table
	const sortedData = useMemo(() => {
		if (!sort) return data;
		const sorted = [...data].sort((a, b) => {
			const aValue = a[sort.key as keyof T];
			const bValue = b[sort.key as keyof T];
			if (aValue === bValue) return 0;
			if (aValue == null) return 1;
			if (bValue == null) return -1;
			if (typeof aValue === 'number' && typeof bValue === 'number') {
				return sort.dir === 'asc' ? aValue - bValue : bValue - aValue;
			}
			return sort.dir === 'asc'
				? String(aValue).localeCompare(String(bValue))
				: String(bValue).localeCompare(String(aValue));
		});
		return sorted;
	}, [data, sort]);

	// Infinite scroll handler
	const handleScroll = useCallback(() => {
		const el = scrollRef.current;
		if (!el || isLoading) return;
		if (totalCount !== undefined && sortedData.length >= totalCount) return;
		
		if (el.scrollHeight <= el.clientHeight) return;
		
		if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) {
			onLoadMore();
		}
	}, [
		onLoadMore,
		isLoading,
		totalCount,
		sortedData.length,
	]);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		el.addEventListener('scroll', handleScroll);
		return () => el.removeEventListener('scroll', handleScroll);
	}, [handleScroll]);

	function handleSort(col: MrcColumn<T>) {
		if (!col.sortable) return;
		setSort((prev) => {
			if (!prev || prev.key !== col.key) {
				return {key: col.key, dir: 'asc'};
			}
			if (prev.dir === 'asc') {
				return {key: col.key, dir: 'desc'};
			}
			return null; // Remove sort
		});
	}

	function handleRowClick(row: T, idx: number) {
		if (onRowClick && onExpand) {
			const isCurrentlyExpanded = expandedRows.has(idx);
			
			// Update expanded rows
			setExpandedRows((prev) => {
				const newSet = new Set(prev);
				if (newSet.has(idx)) {
					newSet.delete(idx);
				} else {
					newSet.add(idx);
				}
				return newSet;
			});
			
			// Call state setters AFTER, not inside setExpandedRows
			if (isCurrentlyExpanded) {
				resetMrcActiveRow();
			} else {
				setActiveMrcRow(row as MrcItem);
			}
			
			onRowClick(row);
		}
		if (onRowClick && !onExpand) {
			onRowClick(row);
			setActiveMrcRow(row as MrcItem);
		}
	}

	function renderCellValue(value: unknown, col?: MrcColumn<T>, row?: T, idx?: number): React.ReactNode {
		if (!col || !row) return '';

		// Custom rendering for process % and avg time to sign columns
		if (col.key === 'process') {
			return getSigningProgressPercent(row as MrcItem);
		}
		if (col.key === 'avg_time_to_sign') {
			return getAvgTimeToSign(row as MrcItem);
		}
		if (col.key === 'created_at') {
			return value ? getDateDefaultFormat(value as string, false) : '';
		}
		if (
			col.key === 'mrc_signed_at' ||
			col.key === 'mrc_rejected_at' ||
			col.key === 'sms_sent_at' ||
			col.key === 'mrc_failed_at'
		) {
			const isExpanded = idx !== undefined && expandedRows.has(idx);
			switch(col.key) {
			case 'mrc_signed_at':
				return value ?
					getDateDefaultFormat(value as string) : '';
			case 'mrc_rejected_at':
				return value ?
					getDateDefaultFormat(value as string) : '';
			case 'sms_sent_at':
				return value ? getDateDefaultFormat(value as string) :
					<NewSCheduleIcon color={isExpanded ? '#FFFFFF' : '#FF8946'} />;
			}
			// switch(col.key) {
			// case 'mrc_signed_at':
			// 	return value ?
			// 		<NewCheckIcon color={isExpanded ? '#FFFFFF' : '#27AD61'} /> : '';
			// case 'mrc_rejected_at':
			// 	return value ?
			// 		<RejectIcon color={isExpanded ? '#FFFFFF' : '#EB5757'} /> : '';
			// case 'sms_sent_at':
			// 	return value ? getDateDefaultFormat(value as string) :
			// 		<NewSCheduleIcon color={isExpanded ? '#FFFFFF' : '#FF8946'} />;
			// }
		}

		if (col?.key === 'status') {
			const isExpanded = idx !== undefined && expandedRows.has(idx);
			if (row.mrc_rejected_at) {
				return <Text color="error" style={{color: isExpanded ? '#FFFFFF' : undefined}}>{t('Rejected')}</Text>;
			}
			if (row.mrc_signed_at) {
				return <Text color="success" style={{color: isExpanded ? '#FFFFFF' : undefined}}>{t('Signed')}</Text>;
			}
			if (row.mrc_failed_at) {
				return <Text color="error" style={{color: isExpanded ? '#FFFFFF' : undefined}}>{t('Failed')}</Text>;
			}
			return <Text color="warning" style={{color: isExpanded ? '#FFFFFF' : undefined}}>{t('Pending')}</Text>;
		}
		
		if (
			typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
		) {
			return value.toString();
		}
		if (React.isValidElement(value)) {
			return value;
		}
		return '-';
	}

	if (isLoading) {
		return (
			<Layout fullWidth alignItems="center" justifyContent="center">
				<Text></Text>
			</Layout>
		);
	}

	if (sortedData.length === 0) {
		return (
			<Layout fullWidth alignItems="center" justifyContent="center" style={{padding: '20px'}}>
				<Text>{t('No data available')}</Text>
			</Layout>
		);
	}

	return (
		<MrcTableWrapper>
			<MrcScrollArea ref={scrollRef}>
				<MrcTable>
					<MrcHeader>
						<MrcTr>
							{columns?.map((col) => (
								<MrcTh
									key={col.key}
									{...(col.width !== undefined ? {width: col.width} : {})}
									style={{
										cursor: col.sortable ? 'pointer' : undefined,
										userSelect: 'none',
									}}
									onClick={() => handleSort(col)}
								>
									<Layout gap="xs" nowrap>
										<Text color="secondary">
											{col.title}
											{col.sortable && (
												sort?.key === col.key
													? (sort.dir === 'asc' ? ' ▲' : ' ▼')
													: ' ⇅'
											)}
										</Text>
									</Layout>
								</MrcTh>
							))}
						</MrcTr>
					</MrcHeader>
					<tbody
						style={{gap: '5px'}}
					>
						{(sortedData as T[]).map((row, idx) => (
							<React.Fragment key={idx}>
								<MrcTrBody
									onClick={
										onRowClick ? () => handleRowClick(row, idx) : undefined
									}
									style={{
										cursor: onRowClick ? 'pointer' : undefined,
										transition: 'background 0.2s',
									}}
								>
									{columns?.map((col, colIdx) => {
										const isError = row.mrc_rejected_at != null;
										const isSigned = row.mrc_signed_at != null;
										const mrcFailed = row.mrc_failed_at != null;

										return (
											<MrcTd
												$expanded={onExpand && expandedRows.has(idx)}
												key={col.key}
												$isFirst={colIdx === 0}
												$isLast={colIdx === columns.length - 1}
												$isError={isError}
												$isSigned={isSigned}
												$mrcFailed={mrcFailed}
											>
												{col.render
													? col.render(row)
													: renderCellValue(row[col.key as keyof T], col, row, idx)}
											</MrcTd>
										);
									})}
								</MrcTrBody>
								{onExpand && (
									<tr>
										<MrcExpandedRowTd $expanded={expandedRows.has(idx)} colSpan={columns.length}>
											<MrcExpandedRowContent $expanded={expandedRows.has(idx)}>
												{typeof renderExpandedRow === 'function' && renderExpandedRow(row)}
											</MrcExpandedRowContent>
										</MrcExpandedRowTd>
									</tr>
								)}
							</React.Fragment>
						))}
					</tbody>
				</MrcTable>
			</MrcScrollArea>
		</MrcTableWrapper>
	);
}

export default MrcCustomTable;